import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { fpColors } from '../../../styles/theme';
import { useAppSelector } from '../../../store/store';
import { selectGroups } from '../groupsSlice';
import type { RootState } from '../../../store/store';
import type { GroupDetail } from '../../../api/types';

/**
 * LentItemRow — Sprint 8 Phase C.
 *
 * Renders one item that the owner has handed off (custody flipped
 * within a crew). Different from ShareRow because:
 *   - There's no share record / share_token — it's a pure custody
 *     handoff inside a crew.
 *   - "Counterparty" is the current custodian, resolved client-side
 *     from the cached crew member list.
 *   - Overdue state shown prominently when `custody_due_date < now`.
 *
 * Tap navigates to ItemDetail where the owner can recall or message
 * the custodian.
 */

export interface LentItemRowProps {
  itemId: number;
  itemName: string;
  custodianUserId: number;
  custodySince?: string | null;
  custodyDueDate?: string | null;
  onPress: () => void;
  testID?: string;
}

export const LentItemRow: React.FC<LentItemRowProps> = ({
  itemId,
  itemName,
  custodianUserId,
  custodySince,
  custodyDueDate,
  onPress,
  testID,
}) => {
  // Resolve the custodian's name from any cached GroupDetail.members.
  // If we haven't visited any crew containing this custodian, fall back
  // to "Someone in your crew" rather than show a raw user_id.
  const custodianName = useAppSelector((s: RootState) =>
    resolveCustodianName(s, custodianUserId),
  );

  const overdueState = computeOverdueState(custodyDueDate);

  const sinceLine = custodySince ? `since ${formatRelative(custodySince)}` : null;
  const dueLine =
    custodyDueDate && overdueState !== 'overdue'
      ? `due ${formatDate(custodyDueDate)}`
      : null;
  const overdueLine =
    overdueState === 'overdue'
      ? `overdue · was due ${formatDate(custodyDueDate!)}`
      : null;

  return (
    <Pressable
      onPress={onPress}
      testID={testID ?? `lent-item-row-${itemId}`}
      style={({ pressed }) => [styles.row, pressed && { opacity: 0.85 }]}
      accessibilityRole="button"
      accessibilityLabel={`${itemName} with ${custodianName}${overdueState === 'overdue' ? ', overdue' : ''}`}
    >
      <View
        style={[
          styles.iconCol,
          overdueState === 'overdue' && {
            backgroundColor: fpColors.errorContainer,
          },
        ]}
      >
        <MaterialIcons
          name="local-shipping"
          size={18}
          color={
            overdueState === 'overdue' ? fpColors.error : fpColors.primary
          }
        />
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={styles.title} numberOfLines={1}>
          {itemName}
        </Text>
        <Text style={styles.subtitle} numberOfLines={1}>
          {[`with ${custodianName}`, overdueLine ?? dueLine ?? sinceLine]
            .filter(Boolean)
            .join(' · ')}
        </Text>
      </View>
      {overdueState === 'overdue' && (
        <View style={styles.overduePill}>
          <Text style={styles.overdueText}>OVERDUE</Text>
        </View>
      )}
      <MaterialIcons
        name="chevron-right"
        size={18}
        color={fpColors.outline}
        style={{ marginLeft: 4 }}
      />
    </Pressable>
  );
};

function resolveCustodianName(
  state: RootState,
  custodianUserId: number,
): string {
  // Look in every cached GroupDetail.members for a matching user_id.
  const details = state.groups.detail as Record<number, GroupDetail>;
  for (const id in details) {
    const detail = details[id];
    if (!detail?.members) continue;
    const m = detail.members.find((x: any) => x.user_id === custodianUserId);
    if (m?.name) {
      // Prefer first name only — these are crew members the user
      // already knows. "Dylan" reads better than "Dylan Kowalski".
      return m.name.split(/\s+/)[0];
    }
  }
  return 'someone in your circle';
}

function computeOverdueState(
  dueDateIso: string | null | undefined,
): 'overdue' | 'due-soon' | 'ok' | 'no-due' {
  if (!dueDateIso) return 'no-due';
  const due = new Date(dueDateIso).getTime();
  if (isNaN(due)) return 'no-due';
  const now = Date.now();
  if (due < now) return 'overdue';
  if (due - now < 1000 * 60 * 60 * 24) return 'due-soon';
  return 'ok';
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatRelative(iso: string): string {
  const t = new Date(iso).getTime();
  if (isNaN(t)) return '';
  const diff = Date.now() - t;
  if (diff < 0) return 'just now';
  const d = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (d < 1) return 'today';
  if (d < 7) return `${d}d`;
  return formatDate(iso);
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 14,
    gap: 12,
    borderBottomWidth: 1,
    borderBottomColor: fpColors.outlineVariant,
    backgroundColor: fpColors.surfaceContainerLowest,
  },
  iconCol: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: fpColors.primaryContainer,
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 14,
    color: fpColors.onSurface,
  },
  subtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    marginTop: 2,
  },
  overduePill: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
    backgroundColor: fpColors.errorContainer,
  },
  overdueText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 10,
    color: fpColors.error,
    letterSpacing: 0.5,
  },
});
